import type { AppRole } from "../../lib/types";

export type AuthSession = {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: {
    id: string;
    email: string;
  };
};

type SupabaseTokenResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user: {
    id: string;
    email?: string;
  };
};

export const AUTH_SESSION_KEY = "vibegenie_supabase_session";

function getSupabaseConfig() {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
  if (!url || !key) {
    throw new Error("Supabase環境変数 VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY が未設定です。");
  }
  return { url: url.replace(/\/$/, ""), key };
}

function toSession(payload: SupabaseTokenResponse): AuthSession {
  return {
    accessToken: payload.access_token,
    refreshToken: payload.refresh_token,
    expiresAt: Date.now() + payload.expires_in * 1000,
    user: {
      id: payload.user.id,
      email: payload.user.email ?? ""
    }
  };
}

function readStoredSession(): AuthSession | null {
  const raw = localStorage.getItem(AUTH_SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthSession;
  } catch {
    localStorage.removeItem(AUTH_SESSION_KEY);
    return null;
  }
}

function storeSession(session: AuthSession) {
  localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
}

function clearStoredSession() {
  localStorage.removeItem(AUTH_SESSION_KEY);
}

async function formatSupabaseError(response: Response, fallback: string) {
  const text = await response.text().catch(() => "");
  if (!text) return `${fallback} (${response.status})`;
  try {
    const payload = JSON.parse(text) as { msg?: string; message?: string; error?: string; error_description?: string; code?: string };
    const message = payload.msg ?? payload.message ?? payload.error_description ?? payload.error ?? text;
    if (response.status === 429 && /email rate limit exceeded/i.test(message)) {
      return `${fallback} Supabaseの確認メール送信制限に達しています。開発中はConfirm emailをOFFにするか、SMTP/Rate Limits設定を調整してください。`;
    }
    return `${fallback} (${response.status}) ${message}${payload.code ? ` [${payload.code}]` : ""}`;
  } catch {
    return `${fallback} (${response.status}) ${text}`;
  }
}

export const authService = {
  async getSession(): Promise<AuthSession | null> {
    const session = readStoredSession();
    if (!session) return null;
    if (session.expiresAt - 60_000 > Date.now()) return session;
    return this.refreshSession(session.refreshToken);
  },

  async signInWithPassword(email: string, password: string): Promise<AuthSession> {
    const { url, key } = getSupabaseConfig();
    const response = await fetch(`${url}/auth/v1/token?grant_type=password`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) throw new Error(await formatSupabaseError(response, "メールアドレスまたはパスワードを確認してください。"));
    const session = toSession((await response.json()) as SupabaseTokenResponse);
    storeSession(session);
    return session;
  },

  async signUpWithPassword(email: string, password: string): Promise<AuthSession | null> {
    const { url, key } = getSupabaseConfig();
    const response = await fetch(`${url}/auth/v1/signup`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) throw new Error(await formatSupabaseError(response, "アカウントを作成できませんでした。"));

    const payload = (await response.json()) as Partial<SupabaseTokenResponse>;
    if (!payload.access_token || !payload.refresh_token || !payload.expires_in || !payload.user?.id) return null;

    const session = toSession(payload as SupabaseTokenResponse);
    storeSession(session);
    return session;
  },

  async sendPasswordRecovery(email: string): Promise<void> {
    const { url, key } = getSupabaseConfig();
    const redirectTo = `${window.location.origin}/reset-password`;
    const response = await fetch(`${url}/auth/v1/recover`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ email, redirect_to: redirectTo })
    });

    if (!response.ok) throw new Error(await formatSupabaseError(response, "パスワード再設定メールを送信できませんでした。"));
  },

  async refreshSession(refreshToken: string): Promise<AuthSession | null> {
    const { url, key } = getSupabaseConfig();
    const response = await fetch(`${url}/auth/v1/token?grant_type=refresh_token`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });

    if (!response.ok) {
      localStorage.removeItem(AUTH_SESSION_KEY);
      return null;
    }

    const session = toSession((await response.json()) as SupabaseTokenResponse);
    storeSession(session);
    return session;
  },

  async signOut(): Promise<void> {
    const session = readStoredSession();
    clearStoredSession();
    if (!session) return;

    const { url, key } = getSupabaseConfig();
    await fetch(`${url}/auth/v1/logout`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${session.accessToken}`
      }
    }).catch(() => undefined);
  },

  async updatePasswordWithRecoveryToken(accessToken: string, password: string): Promise<void> {
    const { url, key } = getSupabaseConfig();
    const response = await fetch(`${url}/auth/v1/user`, {
      method: "PUT",
      headers: {
        apikey: key,
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) throw new Error(await formatSupabaseError(response, "パスワードを更新できませんでした。"));
    clearStoredSession();
  }
};

export async function getAccessToken() {
  return (await authService.getSession())?.accessToken ?? null;
}

export function isAdminRole(role: AppRole | null) {
  return role === "admin";
}

export function getRecoveryAccessTokenFromUrl(url: Pick<Location, "hash" | "search"> = window.location) {
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(url.search);
  const type = hashParams.get("type") ?? searchParams.get("type");
  const accessToken = hashParams.get("access_token") ?? searchParams.get("access_token");

  if (type !== "recovery" || !accessToken) return null;
  return accessToken;
}
